const OKTA_DOMAIN = process.env.REACT_APP_OKTA_DOMAIN as string;
const OKTA_CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID as string;
const OKTA_REDIRECT_URI = process.env.REACT_APP_OKTA_REDIRECT_URI as string;
const API_AUDIENCE = process.env.REACT_APP_API_AUDIENCE as string | undefined;

const STORAGE_KEYS = {
  accessToken: 'okta_access_token',
  idToken: 'okta_id_token',
  expiresAt: 'okta_expires_at',
  nonce: 'okta_oidc_nonce',
  state: 'okta_oidc_state',
  codeVerifier: 'okta_code_verifier',
  lastActive: 'onesec_last_active',
};

/** Force re-login if the user has been inactive for longer than this. */
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

/** Throttle lastActive writes to at most once every 30 seconds. */
const LAST_ACTIVE_THROTTLE_MS = 30 * 1000;
let _lastActiveWritten = 0;

/**
 * Record that the user is actively using the portal right now.
 * Throttled to once per 30 s. Writes to localStorage AND mirrors to a
 * browser cookie so the auth gateway can read it for Risk Management /
 * Security Incident API calls.
 */
export function updateLastActive(): void {
  const now = Date.now();
  if (now - _lastActiveWritten < LAST_ACTIVE_THROTTLE_MS) return;
  _lastActiveWritten = now;
  localStorage.setItem(STORAGE_KEYS.lastActive, now.toString());
  // Non-HttpOnly so the gateway can also set/read it cross-module.
  document.cookie = `onesec_last_active=${now}; path=/; max-age=7200; samesite=lax`;
}

/**
 * Returns true if the user made an API call within the last hour.
 * Checks the browser cookie first (written by the gateway for all modules)
 * then falls back to localStorage (written by apiFetch for Helios Frontend).
 */
export function isActiveWithinLastHour(): boolean {
  const cookieMatch = document.cookie
    .split(';')
    .find(c => c.trim().startsWith('onesec_last_active='));
  if (cookieMatch) {
    const val = cookieMatch.split('=')[1]?.trim();
    if (val && Date.now() - Number(val) < INACTIVITY_TIMEOUT_MS) return true;
  }
  const stored = localStorage.getItem(STORAGE_KEYS.lastActive);
  return !!stored && Date.now() - Number(stored) < INACTIVITY_TIMEOUT_MS;
}

function generateRandomString(length: number, charset?: string) {
  const chars = charset ?? '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';
  const cryptoObj = window.crypto || (window as any).msCrypto;
  if (cryptoObj?.getRandomValues) {
    const randomValues = new Uint32Array(length);
    cryptoObj.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    return result;
  }
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

/** PKCE: generate code_verifier (43–128 chars per spec) */
function generateCodeVerifier(): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  return generateRandomString(64, charset);
}

/** PKCE: code_challenge = base64url(sha256(code_verifier)) */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(hashArray)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Build Okta base URL (no trailing slash) */
function getOktaBaseUrl(): string {
  const domain = OKTA_DOMAIN?.startsWith('https://') ? OKTA_DOMAIN : `https://${OKTA_DOMAIN}`;
  return domain.replace(/\/$/, '');
}

/** Authorization Code + PKCE: build authorize URL (response_type=code) */
export async function buildAuthorizeUrl(): Promise<string> {
  if (!OKTA_DOMAIN || !OKTA_CLIENT_ID || !OKTA_REDIRECT_URI) {
    throw new Error('Auth env vars missing: REACT_APP_OKTA_DOMAIN, REACT_APP_OKTA_CLIENT_ID, REACT_APP_OKTA_REDIRECT_URI');
  }
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const nonce = generateRandomString(32);
  const state = generateRandomString(32);
  sessionStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.nonce, nonce);
  sessionStorage.setItem(STORAGE_KEYS.state, state);
  const params = new URLSearchParams({
    client_id: OKTA_CLIENT_ID,
    redirect_uri: OKTA_REDIRECT_URI,
    response_type: 'code',
    scope: 'openid profile email offline_access',
    state,
    nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (API_AUDIENCE) {
    params.append('audience', API_AUDIENCE);
  }
  const isAuth0 = OKTA_DOMAIN.includes('.auth0.com');
  const authorizePath = isAuth0 ? '/authorize' : '/oauth2/default/v1/authorize';
  const baseUrl = getOktaBaseUrl();
  return `${baseUrl}${authorizePath}?${params.toString()}`;
}

/**
 * Verify the OAuth state CSRF token and return the stored code_verifier.
 * Returns null (and clears storage) if state is missing or mismatched.
 * Used by Callback.tsx when routing the exchange through the backend.
 */
export function verifyAndClearState(stateFromUrl?: string): string | null {
  const storedState = sessionStorage.getItem(STORAGE_KEYS.state);
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.codeVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.codeVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.state);
  sessionStorage.removeItem(STORAGE_KEYS.nonce);
  if (!storedState || !codeVerifier) return null;
  if (stateFromUrl != null && storedState !== stateFromUrl) return null;
  return codeVerifier;
}

/** Exchange authorization code for tokens (Authorization Code + PKCE). Verifies state if provided. */
export async function exchangeCodeForTokens(code: string, stateFromUrl?: string): Promise<void> {
  if (!OKTA_DOMAIN || !OKTA_CLIENT_ID || !OKTA_REDIRECT_URI) {
    throw new Error('Auth env vars missing');
  }
  if (stateFromUrl != null) {
    const storedState = sessionStorage.getItem(STORAGE_KEYS.state);
    if (!storedState || storedState !== stateFromUrl) {
      throw new Error('State mismatch - possible CSRF or expired session');
    }
  }
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.codeVerifier);
  if (!codeVerifier) {
    throw new Error('No code_verifier found; login may have started in a different tab or session');
  }
  const isAuth0 = OKTA_DOMAIN.includes('.auth0.com');
  const tokenPath = isAuth0 ? '/oauth/token' : '/oauth2/default/v1/token';
  const tokenUrl = `${getOktaBaseUrl()}${tokenPath}`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: OKTA_REDIRECT_URI,
    client_id: OKTA_CLIENT_ID,
    code_verifier: codeVerifier,
  });
  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const err = await res.text();
    try {
      const json = JSON.parse(err);
      throw new Error(json.error_description || json.error || err);
    } catch (e) {
      if (e instanceof Error && e.message !== err) throw e;
      throw new Error(err || `Token exchange failed: ${res.status}`);
    }
  }
  const data = await res.json();
  const accessToken = data.access_token;
  const idToken = data.id_token;
  const expiresIn = data.expires_in;
  if (!accessToken || !idToken) {
    throw new Error('Token response missing access_token or id_token');
  }
  const expiresAt = expiresIn ? (Date.now() + Number(expiresIn) * 1000).toString() : '';
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.idToken, idToken);
  if (expiresAt) localStorage.setItem(STORAGE_KEYS.expiresAt, expiresAt);
  sessionStorage.removeItem(STORAGE_KEYS.codeVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.state);
  sessionStorage.removeItem(STORAGE_KEYS.nonce);
}

export function parseTokensFromHash(hashOrQuery: string) {
  if (!hashOrQuery || hashOrQuery.length <= 1) {
    throw new Error('No authentication data found in URL');
  }
  
  // Handle both hash (#) and query (?) formats
  const trimmed = hashOrQuery.startsWith('#') || hashOrQuery.startsWith('?') 
    ? hashOrQuery.substring(1) 
    : hashOrQuery;
  const params = new URLSearchParams(trimmed);
  
  // Check for error parameters first
  const error = params.get('error');
  if (error) {
    const errorDescription = params.get('error_description');
    throw new Error(errorDescription || error);
  }
  
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');
  const expiresIn = params.get('expires_in');
  const state = params.get('state');
  const storedState = localStorage.getItem(STORAGE_KEYS.state);
  
  if (!state) {
    throw new Error('No state parameter in response');
  }
  
  if (!storedState) {
    throw new Error('No stored state found (may have expired)');
  }
  
  if (state !== storedState) {
    throw new Error('State mismatch - possible CSRF attack or expired session');
  }
  
  if (!accessToken || !idToken) {
    throw new Error('Missing authentication tokens in response');
  }
  
  const expiresAt = expiresIn ? (Date.now() + Number(expiresIn) * 1000).toString() : '';
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.idToken, idToken);
  if (expiresAt) localStorage.setItem(STORAGE_KEYS.expiresAt, expiresAt);
  localStorage.removeItem(STORAGE_KEYS.state);
  localStorage.removeItem(STORAGE_KEYS.nonce);
}

export function isAuthenticated(): boolean {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);
  if (!token) return false;
  // Do NOT call logout() here — an expired token may be recoverable via the
  // refresh token when the user was active within the last hour.
  // AuthRoute handles the refresh-or-logout decision asynchronously.
  if (expiresAt && Date.now() > Number(expiresAt)) return false;
  return true;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

/** Store dev-login token (same keys as Okta so getAccessToken/isAuthenticated work). */
export function setDevLoginToken(accessToken: string, expiresIn: number): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  const expiresAt = (Date.now() + expiresIn * 1000).toString();
  localStorage.setItem(STORAGE_KEYS.expiresAt, expiresAt);
}

export function logout() {
  // sendBeacon persists across page navigation so the HttpOnly cookie is
  // cleared server-side even when the caller immediately redirects.
  navigator.sendBeacon('/api/auth/logout');
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.idToken);
  localStorage.removeItem(STORAGE_KEYS.expiresAt);
  localStorage.removeItem(STORAGE_KEYS.lastActive);
  document.cookie = 'onesec_last_active=; path=/; max-age=0; samesite=lax';
}

// ── Silent token refresh ──────────────────────────────────────────────────────

/** Refresh when access token has less than this many ms remaining. */
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

/** Deduplicates concurrent refresh calls — only one request goes to the backend at a time. */
let _refreshPromise: Promise<void> | null = null;

/**
 * Exchange the onesec_okta_refresh_token HttpOnly cookie for a new access token.
 * Updates localStorage on success; calls logout() if the refresh token is expired/revoked.
 * Not called directly — use refreshIfNeeded() instead.
 */
async function doRefresh(): Promise<void> {
  // Inactivity gate: only refresh if the user was active within the last hour.
  if (!isActiveWithinLastHour()) {
    logout();
    return;
  }

  const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  try {
    const res = await fetch(`${apiUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // sends onesec_okta_refresh_token HttpOnly cookie
    });
    if (!res.ok) {
      logout();
      return;
    }
    const data = await res.json();
    if (data.access_token) {
      localStorage.setItem(STORAGE_KEYS.accessToken, data.access_token);
      if (data.id_token) localStorage.setItem(STORAGE_KEYS.idToken, data.id_token);
      if (data.expires_in) {
        const expiresAt = (Date.now() + Number(data.expires_in) * 1000).toString();
        localStorage.setItem(STORAGE_KEYS.expiresAt, expiresAt);
      }
    }
  } catch {
    // Network error — don't logout, the next API call's 401 handler will catch it
  }
}

/**
 * Call before any authenticated API request.
 * Triggers a silent refresh if the access token expires within REFRESH_THRESHOLD_MS.
 * Multiple simultaneous calls are deduplicated — only one refresh request is sent.
 */
export async function refreshIfNeeded(): Promise<void> {
  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);
  if (!expiresAt) return; // no token stored — nothing to refresh
  if (Date.now() < Number(expiresAt) - REFRESH_THRESHOLD_MS) return; // still fresh

  if (!_refreshPromise) {
    _refreshPromise = doRefresh().then(
      () => { _refreshPromise = null; },
      () => { _refreshPromise = null; },
    );
  }
  return _refreshPromise;
}


